import { useMemo } from 'react';
import {
    PriceLike,
    SalesforceProduct,
    SalesforceProductPriceType,
    SalesforceProductPromotionType,
} from '~/lib/salesforce/types';
import { derivePromoType } from '~/lib/salesforce/utilities/prices';
import { useRedirectableCampaign } from './useRedirectableCampaign';
import { useSpecialPrice } from '~/services/special-price';
import { shouldHighlightDefaultPromoPrice } from '~/shared/utils/shouldHighlightDefaultPromoPrice';
import { useTranslation } from '~/shared/utils/translation';

type Sorting = [string, string, string];

interface HookOptions {
    sorting?: Sorting;
}

const defaultSorting: Sorting = [
    SalesforceProductPriceType.DISABLED,
    SalesforceProductPriceType.DEFAULT,
    SalesforceProductPriceType.SALE,
];

export const useProductDisplayPrices = (
    product: SalesforceProduct,
    options?: HookOptions,
): PriceLike[] => {
    // Salesforce is not obliged to send a price list, and the display logic below reads it as
    // a list throughout; an empty one produces no prices, which is what a caller should see.
    const { id, c_activepromo, c_prices = [], c_splash, c_specialPrices } = product;
    const { sorting = defaultSorting } = options || {};

    const { translate } = useTranslation();
    const redirectableCampaign = useRedirectableCampaign(product);

    const { prices: specialPrices = [], isActive: hasSpecialPricing } = useSpecialPrice({
        id,
        c_specialPrices,
        c_prices,
    });

    const hasMemberPrice = c_prices.some(
        (price) => price.promotype === SalesforceProductPromotionType.MEMBER_PRICE,
    );

    const evaluatedPrices: PriceLike[] = useMemo(() => {
        // Campaigns that redirect to their own page show one plain price, without promo styling.
        if (redirectableCampaign) {
            const basicPrice = c_prices?.at(0);

            return basicPrice ? [{ ...basicPrice, type: SalesforceProductPriceType.DEFAULT }] : [];
        }

        if (hasSpecialPricing) return specialPrices;

        // Constant across the list, so it is derived once rather than per price - it re-scans
        // every price and allocates a fresh `splash` object on each call.
        const promoType = derivePromoType({
            promoType: c_activepromo?.type,
            splash: { text: c_splash },
            prices: c_prices,
        });

        return c_prices.map((price) => {
            const priceType = price.type || SalesforceProductPriceType.DEFAULT;

            const shouldHighlightDefaultPrice = shouldHighlightDefaultPromoPrice({
                priceType: price.type,
                promoType,
                prices: c_prices,
            });

            const displayType = shouldHighlightDefaultPrice
                ? SalesforceProductPriceType.SALE
                : priceType;

            // We will display a different label for non-member price (default) on membership prices.
            const displayLabel =
                hasMemberPrice && c_prices.length > 1
                    ? price.promotype === SalesforceProductPromotionType.MEMBER_PRICE
                        ? translate('pdp.memberPrice')
                        : translate('pdp.notMemberPrice')
                    : price.label;

            return {
                ...price,
                promotype: promoType,
                type: displayType,
                label: displayLabel,
            };
        });
    }, [c_prices, c_activepromo, c_splash, hasMemberPrice, specialPrices, redirectableCampaign]);

    const sortedPrices = useMemo(() => {
        // A type outside the sorting trails the ones in it, rather than leading them: `indexOf`
        // answers -1 for an unknown type, which would otherwise outrank every known one.
        const rankOf = (price: PriceLike) => {
            const rank = sorting.indexOf(price.type || SalesforceProductPriceType.DEFAULT);

            return rank === -1 ? sorting.length : rank;
        };

        return [...evaluatedPrices].sort((a, b) => rankOf(a) - rankOf(b));
    }, [evaluatedPrices, sorting]);

    return sortedPrices;
};
